import { useState } from 'react';
import { Loader2, Plus, ExternalLink, CreditCard } from 'lucide-react';
import { cn, isSymbolicaUser } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { useBillingHistory } from '@/hooks/useBillingHistory';
import {
  trackPlanUpgradeClicked,
  trackPlanDowngradeClicked,
  trackPlanChangeConfirmed,
  trackSubscriptionCancelled,
  trackSubscriptionResumed,
  trackManageSubscriptionClicked,
  trackLogout,
} from '@/analytics';
import {
  CancelConfirmModal,
  PlanChangeConfirmModal,
} from '../settings/BillingConfirmModals';
import { PaperSettingsShell } from './PaperSettingsShell';
import { SettingsSection } from './SettingsSection';
import {
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_CLASS,
  PAPER_ICON_COMPACT_CLASS,
  PAPER_ICON_MICRO_CLASS,
  PAPER_ICON_STROKE_WIDTH,
} from './paperShapes';

interface Plan {
  id: string;
  name: string;
  price: number;
  description: string;
}

const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    description: 'Basic access with limited usage budget. Ideal for exploring the platform.',
  },
  {
    id: 'tier1',
    name: 'Tier 1',
    price: 10,
    description: 'Extended usage budget for regular use. Suitable for individual workflows.',
  },
  {
    id: 'tier2',
    name: 'Tier 2',
    price: 50,
    description: 'Higher usage capacity for demanding workloads and longer sessions.',
  },
  {
    id: 'tier3',
    name: 'Tier 3',
    price: 100,
    description: 'Maximum usage allocation for power users and intensive, production-grade usage.',
  },
];

const TIER_ORDER: Record<string, number> = { free: 0, tier1: 1, tier2: 2, tier3: 3 };

const formatPeriodEnd = (unix: number | null) => {
  if (!unix) return '—';
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatInvoiceDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);

interface PlanCardViewModel {
  plan: Plan;
  isCurrent: boolean;
  isPendingStart: boolean;
  cta: { label: string; onClick: () => void; variant: 'default' | 'solid' | 'ghost'; disabled?: boolean } | null;
}

export const BillingSettingsPaper = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const {
    currentPlanId,
    isActive,
    isCanceling,
    isDowngrading,
    pendingPlanId: scheduledPlanId,
    currentPeriodEnd,
    loading: subLoading,
    checkout,
    openPortal,
    cancelSubscription,
    resumeSubscription,
    previewProration,
  } = useSubscription();
  const { methods: paymentMethods } = usePaymentMethods();
  const { events: historyEvents } = useBillingHistory();

  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [prorationPreview, setProrationPreview] = useState<{
    immediateChargeUsd: number;
    currency: string;
  } | null>(null);
  const [prorationLoading, setProrationLoading] = useState(false);

  const currentTierOrder = TIER_ORDER[currentPlanId] ?? 0;

  const symbolicaUser = isSymbolicaUser(user?.email);
  const initials = user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : '';
  const displayName = user ? `${user.firstName} ${user.lastName}` : 'Guest';
  const metaLine = user?.email ?? '';

  const handleCheckout = async (planId: string, direction: 'up' | 'down') => {
    const price = PLANS.find((p) => p.id === planId)?.price ?? 0;
    if (direction === 'up') trackPlanUpgradeClicked(planId, price);
    else trackPlanDowngradeClicked(planId, price);

    if (!isActive) {
      setBusyPlanId(planId);
      try {
        await checkout(planId);
      } finally {
        setBusyPlanId(null);
      }
      return;
    }

    setPendingPlanId(planId);
    setProrationPreview(null);
    if (direction === 'up') {
      setProrationLoading(true);
      try {
        const preview = await previewProration(planId);
        setProrationPreview(preview);
      } catch {
        // fall back to generic message
      } finally {
        setProrationLoading(false);
      }
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!pendingPlanId) return;
    const planId = pendingPlanId;
    const price = PLANS.find((p) => p.id === planId)?.price ?? 0;
    trackPlanChangeConfirmed(planId, price);
    setPendingPlanId(null);
    setBusyPlanId(planId);
    try {
      await checkout(planId);
    } finally {
      setBusyPlanId(null);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      await cancelSubscription();
      trackSubscriptionCancelled();
    } finally {
      setCancelLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const handleResume = async () => {
    setBusyPlanId(currentPlanId);
    try {
      await resumeSubscription();
      trackSubscriptionResumed();
    } finally {
      setBusyPlanId(null);
    }
  };

  const handleKeepPlan = async () => {
    // Reverses a scheduled downgrade by re-subscribing to the current plan.
    setBusyPlanId(currentPlanId);
    try {
      await checkout(currentPlanId);
    } finally {
      setBusyPlanId(null);
    }
  };

  const handlePortal = async () => {
    trackManageSubscriptionClicked();
    setPortalBusy(true);
    try {
      await openPortal();
    } finally {
      setPortalBusy(false);
    }
  };

  const buildPlanView = (plan: Plan): PlanCardViewModel => {
    const currentTier = TIER_ORDER[currentPlanId] ?? 0;
    const thisTier = TIER_ORDER[plan.id] ?? 0;
    const isCurrent = plan.id === currentPlanId && isActive;
    const isPendingStart = plan.id === scheduledPlanId;
    const busy = busyPlanId === plan.id;

    if (isPendingStart) {
      return {
        plan,
        isCurrent: false,
        isPendingStart: true,
        cta: null,
      };
    }

    if (isCurrent) {
      if (isCanceling) {
        return {
          plan,
          isCurrent: true,
          isPendingStart: false,
          cta: { label: 'Reactivate', onClick: handleResume, variant: 'solid', disabled: busy },
        };
      }
      if (scheduledPlanId) {
        return {
          plan,
          isCurrent: true,
          isPendingStart: false,
          cta: { label: 'Keep plan', onClick: handleKeepPlan, variant: 'default', disabled: busy },
        };
      }
      if (plan.id === 'free') {
        return { plan, isCurrent: true, isPendingStart: false, cta: null };
      }
      return {
        plan,
        isCurrent: true,
        isPendingStart: false,
        cta: { label: 'Cancel plan', onClick: () => setShowCancelConfirm(true), variant: 'ghost', disabled: busy },
      };
    }

    if (plan.id === 'free') {
      return { plan, isCurrent: false, isPendingStart: false, cta: null };
    }

    if (!isActive) {
      return {
        plan,
        isCurrent: false,
        isPendingStart: false,
        cta: {
          label: 'Subscribe',
          onClick: () => handleCheckout(plan.id, 'up'),
          variant: 'solid',
          disabled: busy || subLoading,
        },
      };
    }

    if (thisTier > currentTier) {
      return {
        plan,
        isCurrent: false,
        isPendingStart: false,
        cta: {
          label: 'Upgrade',
          onClick: () => handleCheckout(plan.id, 'up'),
          variant: 'solid',
          disabled: busy,
        },
      };
    }

    return {
      plan,
      isCurrent: false,
      isPendingStart: false,
      cta: {
        label: 'Change plan',
        onClick: () => handleCheckout(plan.id, 'down'),
        variant: 'default',
        disabled: busy,
      },
    };
  };

  const planViews = PLANS.map(buildPlanView);

  const primaryCard = paymentMethods && paymentMethods.length > 0 ? paymentMethods[0] : null;

  return (
    <PaperSettingsShell
      activeTab="billing"
      account={{
        initials,
        displayName,
        metaLine,
        onLogout: isAuthenticated ? () => { trackLogout(); logout(); } : undefined,
      }}
    >
      <SettingsSection
        eyebrow="Plans"
        title="Your plan"
        subtitle={
          isActive && !isCanceling && !scheduledPlanId
            ? 'Billed monthly. Change or cancel anytime from this page.'
            : isCanceling
              ? `Your subscription will end on ${formatPeriodEnd(currentPeriodEnd)}.`
              : scheduledPlanId
                ? `A plan change is scheduled for ${formatPeriodEnd(currentPeriodEnd)}.`
                : 'Pick a plan to get started. You can change or cancel anytime.'
        }
      >
        {isActive && !isCanceling && !scheduledPlanId && (
          <div
            className={cn(
              'flex items-center gap-[10px] py-[10px] px-[14px] mb-[14px]',
              'border border-solid border-[var(--hair)] rounded-lg',
              'bg-[var(--surface-2)]',
            )}
          >
            <span className="inline-block w-[6px] h-[6px] rounded-full bg-[var(--periwinkle)]" />
            <span className="text-[12px] text-[var(--fg-3)]">Next billing date</span>
            <span
              className="text-[12px] text-[var(--fg)] ml-auto"
              style={{ fontFamily: 'var(--font-mono-paper)' }}
            >
              {formatPeriodEnd(currentPeriodEnd)}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 desktop:grid-cols-2 gap-[10px]">
          {planViews.map((v) => (
            <PlanCard key={v.plan.id} view={v} />
          ))}
        </div>
      </SettingsSection>

      <SettingsSection
        eyebrow="Payment"
        title="Payment method"
        subtitle="Card on file for monthly charges. Invoices emailed to the workspace admin."
      >
        <div className="flex flex-col gap-[10px]">
          {primaryCard ? (
            <div
              className={cn(
                'py-[12px] px-[14px] flex items-center gap-[12px]',
                'bg-[var(--surface)] border border-solid border-[var(--hair)] rounded-lg',
              )}
            >
              <div
                className={cn(
                  'w-[36px] h-[28px] rounded-[4px] inline-flex items-center justify-center shrink-0',
                  'bg-[var(--bg-alt)] border border-solid border-[var(--hair)] text-[var(--fg-3)]',
                )}
              >
                <CreditCard className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="text-[12px] text-[var(--fg)] tracking-[0.04em] truncate"
                  style={{ fontFamily: 'var(--font-mono-paper)' }}
                >
                  {primaryCard.brand} ···· {primaryCard.last4}
                </div>
                {primaryCard.expiryMonth && primaryCard.expiryYear && (
                  <div
                    className="text-[11px] text-[var(--fg-4)] mt-[2px]"
                    style={{ fontFamily: 'var(--font-mono-paper)' }}
                  >
                    Expires {primaryCard.expiryMonth}/{primaryCard.expiryYear}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handlePortal}
                disabled={portalBusy}
                className={cn(
                  'inline-flex items-center gap-[6px] h-[28px] px-[10px]',
                  PAPER_CONTROL_RADIUS_CLASS,
                  'text-[11px] bg-transparent border border-solid border-transparent',
                  'text-[var(--fg)] transition-colors duration-200',
                  'hover:bg-[var(--periwinkle-wash)] hover:text-[var(--periwinkle)]',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                Edit
              </button>
            </div>
          ) : (
            <div className="text-[12px] text-[var(--fg-3)]">No payment method on file.</div>
          )}

          <button
            type="button"
            onClick={handlePortal}
            disabled={portalBusy}
            className={cn(
              'h-[34px] px-[12px] inline-flex items-center gap-[6px] justify-start',
              'text-[12px] text-[var(--fg-3)]',
              PAPER_CONTROL_RADIUS_CLASS,
              'border border-dashed border-[var(--hair)] bg-transparent cursor-pointer',
              'transition-colors duration-200',
              'hover:text-[var(--fg)] hover:border-[var(--hair-strong)]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <Plus className={PAPER_ICON_COMPACT_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
            Add payment method
          </button>

          <div className="flex justify-end mt-[4px]">
            <button
              type="button"
              onClick={handlePortal}
              disabled={portalBusy}
              className={cn(
                'inline-flex items-center gap-[6px] h-[28px] px-[12px]',
                PAPER_CONTROL_RADIUS_CLASS,
                'text-[11px] font-medium',
                'bg-transparent text-[var(--fg)] border border-solid border-[var(--hair)]',
                'transition-colors duration-200',
                'hover:bg-[var(--periwinkle-wash)] hover:text-[var(--periwinkle)] hover:border-[var(--periwinkle)]',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              {portalBusy ? (
                <Loader2
                  className={cn(PAPER_ICON_MICRO_CLASS, 'animate-spin')}
                  strokeWidth={PAPER_ICON_STROKE_WIDTH}
                />
              ) : (
                <>
                  Manage subscription
                  <ExternalLink className={PAPER_ICON_MICRO_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
                </>
              )}
            </button>
          </div>
        </div>
      </SettingsSection>

      {symbolicaUser && historyEvents.length > 0 && (
        <SettingsSection
          eyebrow="History"
          title="Invoices"
          subtitle="Download itemized PDFs for your records."
        >
          <div
            className={cn(
              'border border-solid border-[var(--hair)] rounded-lg overflow-x-auto',
              'bg-[var(--surface)]',
            )}
          >
            <div
              className={cn(
                'grid grid-cols-[140px_1fr_100px] items-center gap-[12px]',
                'py-[10px] px-[14px] border-b border-solid border-[var(--hair)] bg-[var(--surface-2)]',
                'text-[10px] tracking-[0.14em] uppercase text-[var(--fg-3)]',
              )}
            >
              <div>Date</div>
              <div>Description</div>
              <div className="text-right">Amount</div>
            </div>
            {historyEvents.map((ev, i) => (
              <div
                key={ev.id}
                className={cn(
                  'grid grid-cols-[140px_1fr_100px] items-center gap-[12px]',
                  'py-[12px] px-[14px] text-[12px]',
                  i === historyEvents.length - 1
                    ? ''
                    : 'border-b border-solid border-[var(--hair-subtle)]',
                )}
              >
                <div
                  className="text-[var(--fg-2)]"
                  style={{ fontFamily: 'var(--font-mono-paper)' }}
                >
                  {formatInvoiceDate(ev.date)}
                </div>
                <div className="flex items-center gap-[10px] min-w-0">
                  <span
                    className={cn(
                      'text-[9px] tracking-[0.14em] uppercase font-medium',
                      'py-[2px] px-[6px] rounded-full',
                      'bg-[var(--periwinkle-veil)] text-[var(--periwinkle)]',
                    )}
                  >
                    {ev.status}
                  </span>
                  <span
                    className="text-[var(--fg-2)] truncate"
                    style={{ fontFamily: 'var(--font-mono-paper)' }}
                  >
                    {ev.description}
                  </span>
                  {ev.downloadUrl && (
                    <a
                      href={ev.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        'text-[11px] text-[var(--fg-3)] border-b border-solid border-[var(--hair)] pb-px shrink-0',
                        'hover:text-[var(--fg)]',
                      )}
                      style={{ fontFamily: 'var(--font-mono-paper)' }}
                    >
                      PDF
                    </a>
                  )}
                </div>
                <div
                  className="text-[var(--fg)] text-right"
                  style={{ fontFamily: 'var(--font-mono-paper)' }}
                >
                  {formatMoney(ev.amount, ev.currency)}
                </div>
              </div>
            ))}
          </div>
        </SettingsSection>
      )}

      {showCancelConfirm && (
        <CancelConfirmModal
          currentPeriodEnd={currentPeriodEnd}
          cancelLoading={cancelLoading}
          onCancel={handleCancel}
          onKeep={() => setShowCancelConfirm(false)}
        />
      )}

      {pendingPlanId && (() => {
        const pendingPlan = PLANS.find((p) => p.id === pendingPlanId);
        if (!pendingPlan) return null;
        const pendingOrder = TIER_ORDER[pendingPlanId] ?? 0;
        return (
          <PlanChangeConfirmModal
            plan={pendingPlan}
            isPendingDowngrade={pendingOrder < currentTierOrder}
            currentPeriodEnd={currentPeriodEnd}
            isDowngrading={isDowngrading}
            isCanceling={isCanceling}
            prorationLoading={prorationLoading}
            prorationPreview={prorationPreview}
            onConfirm={handleConfirmPlanChange}
            onCancel={() => {
              setPendingPlanId(null);
              setProrationPreview(null);
            }}
          />
        );
      })()}
    </PaperSettingsShell>
  );
};

interface PlanCardProps {
  view: PlanCardViewModel;
}

const PlanCard = ({ view }: PlanCardProps) => {
  const { plan, isCurrent, isPendingStart, cta } = view;
  const suffix = plan.price === 0 ? '' : '/ month';
  const price = `$${plan.price}`;

  return (
    <div
      className={cn(
        'p-[18px] rounded-lg relative',
        isCurrent
          ? 'border-[1.5px] border-solid border-[var(--periwinkle)] bg-[var(--periwinkle-wash)]'
          : 'border border-solid border-[var(--hair)] bg-[var(--surface)]',
      )}
    >
      <div className="flex items-center gap-[8px]">
        <div
          className={cn(
            'text-[11px] tracking-[0.14em] uppercase font-normal',
            isCurrent ? 'text-[var(--periwinkle)]' : 'text-[var(--fg-3)]',
          )}
        >
          {plan.name}
        </div>
        {isCurrent && (
          <span
            className={cn(
              'text-[9px] tracking-[0.14em] uppercase font-medium',
              'py-[2px] px-[6px] rounded-full',
              'bg-[var(--periwinkle-veil)] text-[var(--periwinkle)]',
            )}
          >
            Current
          </span>
        )}
        {isPendingStart && (
          <span
            className={cn(
              'text-[9px] tracking-[0.14em] uppercase font-medium',
              'py-[2px] px-[6px] rounded-full',
              'bg-[var(--periwinkle-veil)] text-[var(--periwinkle)]',
            )}
          >
            Starting soon
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-[6px] mt-[8px]">
        <span
          className="text-[32px] text-[var(--fg)]"
          style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}
        >
          {price}
        </span>
        {suffix && (
          <span
            className="text-[11px] text-[var(--fg-4)]"
            style={{ fontFamily: 'var(--font-mono-paper)' }}
          >
            {suffix}
          </span>
        )}
      </div>
      <p className="text-[12px] leading-[1.45] text-[var(--fg-3)] mt-[10px]">
        {plan.description}
      </p>
      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          disabled={cta.disabled}
          className={cn(
            'w-full h-[32px] mt-[14px] justify-center inline-flex items-center gap-[6px]',
            PAPER_CONTROL_RADIUS_CLASS,
            'text-[11px] font-medium tracking-[0.02em] cursor-pointer',
            'transition-colors duration-200',
            'border border-solid',
            cta.variant === 'solid' &&
              'bg-[var(--fg)] text-[var(--fg-inverse)] border-[var(--fg)] hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]',
            cta.variant === 'default' &&
              'bg-transparent text-[var(--fg)] border-[var(--hair)] hover:bg-[var(--periwinkle-wash)] hover:text-[var(--periwinkle)] hover:border-[var(--periwinkle)]',
            cta.variant === 'ghost' &&
              'bg-transparent text-[var(--fg)] border-transparent hover:bg-[var(--periwinkle-wash)] hover:text-[var(--periwinkle)]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {cta.disabled ? (
            <Loader2
              className={cn(PAPER_ICON_MICRO_CLASS, 'animate-spin')}
              strokeWidth={PAPER_ICON_STROKE_WIDTH}
            />
          ) : cta.label}
        </button>
      )}
    </div>
  );
};
